// Auth context stores date in Firebase Auth and profile in Firebase Firestore

import React, { useContext, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { navigate } from 'gatsby';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  getAuth,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signInWithRedirect,
  signInWithPopup,
  OAuthProvider,
  getRedirectResult,
  GoogleAuthProvider,
  browserSessionPersistence,
  browserLocalPersistence,
  setPersistence,
} from 'firebase/auth';
import { doc, setDoc, getDoc, onSnapshot, updateDoc, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, profileDatabaseName } from '../firebase';
import { useSite } from './SiteContext';
import { getDefaultSkills } from '../utils/skill';
// eslint-disable-next-line import/no-cycle
import {
  getActiveBadgeById,
  getBadgesByTrigger,
  isBadgeComplete,
  isBadgeLevelCompleted,
  mergeBadgeState,
} from '../utils/badges';
import { trackEvent } from '../utils/track';
import {
  CLEVER_OIDC_PROVIDER_ID,
  PASSWORD_PROVIDER_ID,
  GOOGLE_PROVIDER_ID,
  parseGoogleUsername,
  parseCleverUsername,
} from '../utils/user';
import { DASHBOARD_TYPE_CLEVER, DASHBOARD_TYPE_MANUAL } from '../utils/dashboard';
import { getClassCode, removeClassCode } from '../utils/class-code';

/**
 * Create Auth Context
 *
 * @type {React.Context<unknown>}
 */
const AuthContext = React.createContext();

/**
 * Export Auth context for implementation
 *
 * @returns {Object}
 */
export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Default profile structure. Used when user is created or if user is anonymous.
 * @type {
 * {
 *  badges: Array.<Object>, Array of badge progress and completion information
 *  skills: Object, Skills explorer results
 *  takenQuestionnaire: Boolean, If user has been SHOWN (not necessarily TAKEN) the questionnare
 *  birthYear: null|Number, User birth year
 *  completedSkills: Boolean,
 *  watchedIntro: Boolean,
 *  avatar: String,
 *  username: String,
 *  consumedContent: *[]
 *  }
 * }
 */
function defaultProfile() {
  return {
    avatar: null,
    username: null,
    watchedIntro: false,
    takenQuestionnaire: false,
    completedSkills: false,
    birthYear: null,
    consumedContent: [],
    favoriteContent: [],
    badges: [],
    powerSkills: [],
    skills: getDefaultSkills(),
  };
}

/**
 * Convert Firebase psuedo email address to username
 *
 * @param {string} email
 * @returns {string}
 */
export function emailToUsername(email) {
  return email.substring(2).replace(/@timeforkids\.com$/, '');
}

/**
 * Convert username to Firebase psuedo email address
 *
 * @param {string} username
 * @returns {string}
 */
export function usernameToEmail(username) {
  return `__${username}@timeforkids.com`;
}

export function AuthProvider({ children }) {
  /**
   * @type {[Object, Function]} Firebase Auth user object
   */
  const [user, setUser] = useState(null);

  /**
   * @type {[Boolean, Function]} Is Auth state resolved. Does not indicate user is logged in; only if resolved.
   */
  const [isLoaded, setIsLoaded] = useState(false);

  /**
   * @type {[Object, Function]} profile object
   */
  const [profile, setProfile] = useState(defaultProfile());

  /**
   * @type {Function} add alert
   */
  const { addAlert, pathPrefix, joinClassroom, clearApiCache } = useSite();

  /**
   * @type {[null|string, Function]} Clever user id if applicable
   */
  const [cleverUserId, setCleverUserId] = useState(null);

  /**
   * Escape string for regex
   *
   * @param string
   * @returns {string}
   */
  function escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }

  /**
   * @type {[Object, Function]} stashed URL to redirect to on log in / sign up
   */
  const { stashURL, setStashURL } = useSite();

  /**
   * Validate user-submitted email address via server-side cloud function
   *
   * @param {string} email
   * @returns {Promise<void>}
   */
  async function validateEmail(email) {
    await httpsCallable(functions, 'validateEmail')({ email });
  }

  /**
   * Set opt ins
   *
   * @param {string} email
   */
  async function setOptIns(email) {
    await httpsCallable(functions, 'setOptIns')({ email });
  }

  /**
   * Set user email via server-side cloud function
   *
   * @param {string} email
   * @returns {Promise<void>}
   */
  async function setEmail(email, hashEmail = true) {
    await httpsCallable(functions, 'setEmail')({ email, hash: hashEmail });
  }

  /**
   * Create user account in Firebase auth
   *
   * @param {string} username
   * @param {string} password
   * @param {string} email
   * @param {boolean} hashEmail
   * @param {Object} data
   * @returns {Promise<void>}
   */
  async function signUp(username, password, email, hashEmail, data) {
    await validateEmail(email);
    await createUserWithEmailAndPassword(auth, usernameToEmail(username), password);

    // New user, we can safely create profile without fear of overwriting existing profile
    const ref = doc(db, profileDatabaseName, auth.currentUser.uid);

    data.version = 2;
    data.username = username;
    data.createdAt = Timestamp.now();

    await setDoc(ref, data);

    try {
      setEmail(email, hashEmail);
    } catch (e) {
      // Error silently
    }
  }

  /**
   * Log in user with Firebase auth
   *
   * @param {string} username
   * @param {string} password
   * @returns {Promise<void>}
   */
  async function signIn(username, password) {
    await setPersistence(auth, browserLocalPersistence);
    await signInWithEmailAndPassword(auth, usernameToEmail(username), password);
    removeClassCode();
  }

  /**
   * Login in user with Google
   */
  async function signInGoogle() {
    const provider = new GoogleAuthProvider();
    signInWithRedirect(auth, provider);
  }

  /**
   * Sign in Google complete new user
   *
   * @param {Object} userData
   * @return {Object|Boolean}
   */
  async function signInGoogleCompleteNew(userData) {
    return {
      username: parseGoogleUsername(userData),
      version: 2,
      createdAt: Timestamp.now(),
      registrationType: 'google',
    };
  }

  /**
   * Sign in Google complete returning user
   *
   * @param {Object} userData
   * @return {Object|Boolean}
   */
  async function signInGoogleCompleteReturning(userData) {
    const classCode = getClassCode();

    if (classCode) {
      try {
        joinClassroom(classCode);
      } catch (error) {
        // Intentionally error silently
      }
    }

    return {
      username: parseGoogleUsername(userData),
    };
  }

  /**
   * Sign in google complete
   * Method is triggered when a Google user is logged in.
   * Need to determine if user is new and if so handle some new-user tasks
   *
   * @param {Object} userData
   */
  async function signInGoogleComplete(userData) {
    // Verify userData has required data
    if (!userData || !userData.uid) {
      throw new Error('Token failed');
    }

    // Get existing profile
    let userProfile;
    let ref;
    try {
      ref = doc(db, profileDatabaseName, userData.uid);
      userProfile = await getDoc(ref);
    } catch (error) {
      throw new Error('Unable to retrieve profile');
    }

    // Create or update profile if necessary
    let data = false;
    if (userProfile.exists()) {
      data = await signInGoogleCompleteReturning(userData, userProfile);
    } else {
      data = await signInGoogleCompleteNew(userData);
    }

    if (typeof data === 'object') {
      try {
        await setDoc(ref, data, { merge: true });
      } catch (error) {
        throw new Error('Unable to create profile');
      }
    }
  }

  /**
   * Accept redirect back from School Passport login
   *
   * @returns {Promise<UserCredential>}
   */
  async function signInGoogleRedirect() {
    const result = await getRedirectResult(auth);

    if (!result) {
      throw new Error('No results from getRedirectResult.');
    }

    await signInGoogleComplete(result);

    return result;
  }

  /**
   * Sign in Google via popup
   *
   * @returns {Promise<UserCredential>}
   */
  async function signInGooglePopup() {
    const result = await signInWithPopup(auth, new GoogleAuthProvider());

    await signInGoogleComplete(result);

    return result;
  }


  /**
   * Log in user with OIDC powered by Clever
   */
  async function signInClever() {
    await setPersistence(auth, browserSessionPersistence);
    const provider = new OAuthProvider(CLEVER_OIDC_PROVIDER_ID);
    signInWithRedirect(auth, provider);
  }

  /**
   * Sign in Clever complete new user
   *
   * @param {Object} tokenResult
   * @return {Object|Boolean}
   */
  async function signInCleverCompleteNew(tokenResult) {
    return {
      username: parseCleverUsername(tokenResult),
      version: 2,
      createdAt: Timestamp.now(),
      registrationType: 'clever',
    };
  }

  /**
   * Sign in Clever complete returning user
   *
   * @param {Object} tokenResult
   * @return {Object|Boolean}
   */
  async function signInCleverCompleteReturning(tokenResult) {
    return {
      username: parseCleverUsername(tokenResult),
    };
  }

  /**
   * Sign in Clever complete
   * Method is triggered when a Clever user is logged in or on auth event
   * Need to determine if user is new and if so hand some new-user tasks
   * Method may be called on existing or new users
   *
   * @param {object} token
   */
  async function signInCleverComplete(token = false) {
    // If token is not provided, retrieve it
    const tokenResult = token || (await auth.currentUser.getIdTokenResult());

    // Verify tokenResult has required data
    if (
      !tokenResult ||
      !tokenResult.claims ||
      !tokenResult.claims.firebase ||
      !tokenResult.claims.firebase.sign_in_attributes ||
      !tokenResult.claims.firebase.sign_in_attributes.multi_role_user_id
    ) {
      throw new Error('Token failed');
    }

    // Get Clever ID
    const cleverId = tokenResult.claims.firebase.sign_in_attributes.multi_role_user_id;

    // Get existing profile
    let userProfile;
    let ref;
    try {
      ref = doc(db, profileDatabaseName, cleverId);
      userProfile = await getDoc(ref);
    } catch (error) {
      throw new Error('Unable to retrieve profile');
    }

    // Create or update profile if necessary
    let data = false;
    if (userProfile.exists()) {
      data = await signInCleverCompleteReturning(tokenResult, userProfile);
    } else {
      data = await signInCleverCompleteNew(tokenResult);
    }

    if (typeof data === 'object') {
      try {
        await setDoc(ref, data, { merge: true });
      } catch (error) {
        throw new Error('Unable to create profile');
      }
    }

    // Create user auth row and add role
    try {
      await httpsCallable(functions, 'signInCleverComplete')();
    } catch (error) {
      throw new Error('Unable to complete Clever sign up');
    }

    removeClassCode();
  }

  /**
   * Accept redirect back from Clever login
   *
   * @returns {Promise<UserCredential>}
   */
  async function signInCleverRedirect() {
    const result = await getRedirectResult(auth);

    if (!result) {
      throw new Error('No results from getRedirectResult.');
    }

    await signInCleverComplete();

    return result;
  }

  /**
   * Log out user with Firebase auth
   *
   * @returns {Promise<*>}
   */
  async function signOut() {
    return firebaseSignOut(auth);
  }

  /**
   * Set password with Firebase auth
   *
   * @param {string} password
   * @returns {Promise<*>}
   */
  async function setPassword(password) {
    return updatePassword(auth.currentUser, password);
  }

  /**
   * Reauthenticate uuser with Firebase auth
   * In some circumstances (e.g. changing password), current user may be required to reauthenticate.
   * Typical scenario is if the user has been logged in a for an extended amount of time.
   *
   * @param {string} password
   * @returns {Promise<*>}
   */
  async function reauthenticate(password) {
    const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
    return reauthenticateWithCredential(auth.currentUser, credential);
  }

  /**
   * Redirect to stash URL if exist or go home
   *
   * @param defaultUrl
   */
  function redirectStashURL(defaultUrl = '/') {
    const re = new RegExp(`^${escapeRegExp(pathPrefix)}`);

    if (stashURL) {
      const localStashURL = stashURL;
      setStashURL(null);
      navigate(localStashURL.replace(re, ''));
    } else {
      navigate(defaultUrl.replace(re, ''));
    }
  }

  /**
   * Send magic sign in link to user to recover lost account via server-side cloud function
   *
   * @param {string} email
   * @returns {Promise<void>}
   */
  async function sendLink(email) {
    await httpsCallable(functions, 'recoverAccount')({ email });
  }

  /**
   * Log in user via magic link to recover lost account
   *
   * @returns {Promise<void>}
   */
  async function acceptLink(username) {
    const authObj = getAuth();

    if (isSignInWithEmailLink(authObj, window.location.href)) {
      await signInWithEmailLink(authObj, usernameToEmail(username), window.location.href);
    } else {
      throw new Error('An error occurred while resetting the password. Please try again.');
    }
  }

  /**
   * Update profile stored in Firebase Firestore
   *
   * @param {Object} data
   */
  function updateProfile(data) {
    // Firebase password users will use uid for profile key.
    // Firebase OIDC (Clever) users will use clever user id for profile key.
    updateDoc(doc(db, profileDatabaseName, cleverUserId || auth.currentUser.uid), {
      ...data,
      ...{ updatedAt: Timestamp.now() },
    });
  }

  /**
   * Set avatar
   * @param {String} avatar
   */
  function setAvatar(avatar) {
    updateProfile({ avatar });
  }

  /**
   * Set watched intro
   * @param {Boolean} watchedIntro
   */
  function setWatchedIntro(watchedIntro) {
    updateProfile({ watchedIntro });
  }

  /**
   * Set taken questionnaire
   * @param {Boolean} takenQuestionnaire
   */
  function setTakenQuestionnaire(takenQuestionnaire) {
    updateProfile({ takenQuestionnaire });
  }

  /**
   * Set completed skills, indicating the user has completed skills explorer
   * @param {Boolean} completedSkills
   */
  function setCompletedSkills(completedSkills) {
    updateProfile({ completedSkills });
  }

  /**
   * Set consumed content
   * @param {String} id
   */
  function setConsumedContent(id) {
    if (profile.consumedContent.indexOf(id) === -1) {
      updateProfile({ consumedContent: [...profile.consumedContent, id] });
    }
  }

  /**
   * Set favorite content
   * @param {String} id
   * @param {Boolean} false
   */
  function setFavoriteContent(id, add = false) {
    if (!Object.prototype.hasOwnProperty.call(profile, 'favoriteContent')) return;
    let favoriteContent = [...profile.favoriteContent];

    if (add && !favoriteContent.includes(id)) {
      favoriteContent.push(id);
    } else if (!add) {
      favoriteContent = favoriteContent.filter((oneId) => oneId !== id);
    }

    updateProfile({ favoriteContent });
  }

  /**
   * Set skills
   * @param {Object} payload
   */
  function setSkills(payload) {
    const skills = { ...getDefaultSkills(), ...profile.skills };

    Object.keys(payload).forEach((k) => {
      if (
        skills[k] instanceof Object &&
        payload[k] instanceof Object &&
        payload[k].value >= 0 &&
        payload[k].value <= 30
      ) {
        skills[k].value = payload[k].value;
      }
    });

    updateProfile({ skills });
  }

  /**
   * Set badge level completed
   * User has completed a specific level within a badge
   * @param {Number} badgeId
   * @param {String} levelId
   */
  function setBadgeLevelCompleted(badgeId, levelId) {
    const badgeState = profile.badges.find(({ id }) => id === badgeId);

    // Create badge levels if it doesn't exist
    if (!badgeState.levels) {
      badgeState.levels = [];
    }

    let level = badgeState.levels.find(({ id }) => id === levelId);

    // If level does not exist, create it
    if (!level) {
      badgeState.levels.push({ id: levelId });
      level = badgeState.levels[badgeState.levels.length - 1];
    }

    // Add completed to level
    level.completed = Timestamp.now();
  }

  /**
   * Set badge completed
   * User has completed the badge, and all levels within
   * @param {Number} badgeId
   */
  function setBadgeCompleted(badgeId) {
    const badgeState = profile.badges.find(({ id }) => id === badgeId);

    badgeState.completed = Timestamp.now();
  }

  /**
   * Compute completion status of badge levels
   * @param {Number} badgeId
   */
  function computeBadgeLevelsCompleted(badgeId) {
    const badge = mergeBadgeState(
      getActiveBadgeById(badgeId),
      profile.badges.find((b) => b.id === badgeId)
    );

    // Skip if badge is already complete
    if (badge.completed) {
      return;
    }

    // Determine if the levels are complete
    badge.levels.forEach((level) => {
      if (!level.completed && isBadgeLevelCompleted(badge, level)) {
        trackEvent('Skills Explorer', 'Digital badge earned by user', `${badge.name} ${level.id}`);
        setBadgeLevelCompleted(badgeId, level.id);
        addAlert({ id: badgeId, level: level.id });
      }
    });
  }

  /**
   * Compute completion status of badge
   * @param {Number} badgeId
   */
  function computeBadgeCompleted(badgeId) {
    const badge = mergeBadgeState(
      getActiveBadgeById(badgeId),
      profile.badges.find((b) => b.id === badgeId)
    );

    // Skip if badge is already complete
    if (badge.completed) {
      return;
    }

    // Determine if all levels are complete
    if (isBadgeComplete(badge)) {
      setBadgeCompleted(badge.id);
    }
  }

  /**
   * Add progress to badge
   * @param {Number} badgeId
   * @param {String} progressId
   * @param defer
   */
  function addBadgeProgress(badgeId, progressId, defer = false) {
    const badge = getActiveBadgeById(badgeId);
    const badgeState = profile.badges.find(({ id }) => id === badgeId);
    const progress = badgeState && badgeState.progress ? badgeState.progress : [];

    // Skip if badge does not exist
    if (!badge) {
      return;
    }

    // Skip if progress is duplicated on badge
    if (progress.indexOf(progressId) !== -1) {
      return;
    }

    // Skip if badge is already completed
    if (badgeState && badgeState.completed) {
      return;
    }

    // Enforce allowedProgress if applicable
    if (badge.allowedProgress && badge.allowedProgress.indexOf(progressId) < 0) {
      return;
    }

    // Add payload progress to progress array
    progress.push(progressId);

    // If badge does not exist in state, push object
    if (!badgeState) {
      profile.badges.push({ id: badgeId, progress });
    }

    // Calculate completion
    computeBadgeLevelsCompleted(badgeId);
    computeBadgeCompleted(badgeId);

    if (!defer) {
      updateProfile({ badges: profile.badges });
    }
  }

  /**
   * Add power skill
   */
  function addPowerSkill(id, answers, grade, passed) {
    const powerSkills = [
      ...profile.powerSkills.map((a) => {
        return { ...a };
      }),
    ];

    const existingPowerSkill = powerSkills.find((powerSkill) => powerSkill.id === id);
    const date = new Date().toISOString();

    if (existingPowerSkill) {
      existingPowerSkill.id = id;
      existingPowerSkill.answers = answers;
      existingPowerSkill.grade = grade;
      existingPowerSkill.passed = passed;
      existingPowerSkill.date = date;
    } else {
      powerSkills.push({
        id,
        answers,
        grade,
        passed,
        date,
      });
    }

    updateProfile({ powerSkills });
  }

  /**
   * Add quick tip to display
   * Will be either skills-explorer or badge quick tip
   * @returns {undefined}
   */
  function addQuickTipAlert() {
    const level = 'quick-tip';

    if (isLoaded && !profile.completedSkills) {
      addAlert({ id: 'skills-explorer', level });
    } else if (isLoaded && !profile.avatar && !profile.user.username) {
      addAlert({ id: 'profile', level });
    }

    return undefined;
  }

  /**
   * Get provider from request
   *
   * @param {Object} u
   * @param {Boolean} allowDev
   * @return {String}
   */
  function getProviderFromUser(u, allowDev = true) {
    let provider = false;

    if (u && u.providerData && u.providerData[0] && u.providerData[0].providerId) {
      provider = u.providerData[0].providerId;
    }

    if (
      allowDev &&
      process.env.GATSBY_APP_ENV === 'local-development' &&
      u &&
      u.reloadUserInfo &&
      u.reloadUserInfo.customAttributes
    ) {
      try {
        const customAttributes = JSON.parse(u.reloadUserInfo.customAttributes);

        if (customAttributes && customAttributes.dev_sign_in_provider) {
          provider = customAttributes.dev_sign_in_provider;
        }
      } catch (err) {
        // Intentionally error silently
      }
    }

    return provider;
  }

  /**
   * is google user
   *
   * @param {Object} u
   * @return {Boolean}
   */
  function isGoogleUser(u = user) {
    return getProviderFromUser(u) === GOOGLE_PROVIDER_ID;
  }

  /*
   * Is password user
   *
   * @param {Object} u
   * @return {Boolean}
   */
  function isPasswordUser(u = user) {
    return getProviderFromUser(u) === PASSWORD_PROVIDER_ID;
  }

  /**
   * Is clever user
   *
   * @param {Object} u
   * @return {Boolean}
   */
  function isCleverUser(u = user) {
    return getProviderFromUser(u) === CLEVER_OIDC_PROVIDER_ID;
  }

  /**
   * Dashboard type
   */
  function dashboardType() {
    if (isGoogleUser() || isPasswordUser()) {
      return DASHBOARD_TYPE_MANUAL;
    }

    if (isCleverUser()) {
      return DASHBOARD_TYPE_CLEVER;
    }

    return false;
  }

  /**
   * Sign up additional data
   *
   * @param {Object} data
   */
  async function signUpAdditionalData(data) {
    updateProfile(data);
  }

  /**
   * Get school from id
   *
   * @param {string} schoolId
   * @returns {Promise<Object>}
   */
  async function getSchoolFromId(schoolId) {
    const response = await httpsCallable(functions, 'getSchoolFromId')({ schoolId });
    const school = response.data.school || {};

    const data = {
      ...(school.schoolid && { schoolid: school.schoolid }),
      ...(school.schoolName && { schoolName: school.schoolName }),
      ...(school.address?.city && { city: school.address.city }),
      ...(school.address?.zip && { zipcode: school.address.zip }),
      ...(school.address?.state && { state: school.address.state }),
      ...(school.schoolLevel && { schoolLevel: school.schoolLevel }),
      ...(school.isTitleISchool && { isTitleISchool: school.isTitleISchool }),
      ...(school.isTitleISchoolwideSchool && { isTitleISchoolwideSchool: school.isTitleISchoolwideSchool }),
      ...(school.district?.districtID && { districtID: school.district.districtID }),
      ...(school.district?.districtName && { districtName: school.district.districtName }),
      ...(school.schoolYearlyDetails && { schoolYearlyDetails: school.schoolYearlyDetails }),
      ...(school.isCharterSchool && { isCharterSchool: school.isCharterSchool }),
      ...(school.isMagnetSchool && { isMagnetSchool: school.isMagnetSchool }),
      ...(school.isPrivate && { isPrivate: school.isPrivate }),
    };

    return data;
  }

  /**
   * Create Firebase auth event handler to listen for changes in authentication state
   */
  useEffect(() => {
    const authEventHandler = onAuthStateChanged(auth, (eventUser) => {
      if (eventUser) {
        if (getProviderFromUser(eventUser, false) === CLEVER_OIDC_PROVIDER_ID) {
          // Determine if current user is OIDC (Clever). If so, set cleverUserId
          // Intentionally avoid use of isCleverUser() in order to disable allowDev
          eventUser
            .getIdTokenResult()
            .then((tokenResult) => {
              if (
                tokenResult &&
                tokenResult.claims &&
                tokenResult.claims.firebase &&
                tokenResult.claims.firebase.sign_in_attributes &&
                tokenResult.claims.firebase.sign_in_attributes.multi_role_user_id
              ) {
                setCleverUserId(tokenResult.claims.firebase.sign_in_attributes.multi_role_user_id);
                setUser(eventUser);

                // Call signInCleverComplete in case there was a failure during sign in
                signInCleverComplete(tokenResult).catch((error) => {
                  // eslint-disable-next-line no-console
                  console.log(error);
                  // eslint-disable-next-line no-alert
                  alert(
                    'An error occurred completing sign in. Try again or notify your teacher. If you are an educator and need support, please visit the FAQ for further assistance.'
                  );
                  throw new Error('Unable to complete login');
                });
              } else {
                // eslint-disable-next-line no-alert
                alert(
                  'An error occurred during sign in. Try again or notify your teacher. If you are an educator and need support, please visit the FAQ for further assistance.'
                );

                throw new Error('Unable to login');
              }
            })
            .catch((e) => {
              // Intentionally log console
              console.log(e); //eslint-disable-line
              clearApiCache();
              setUser(null);
              setCleverUserId(null);
              setIsLoaded(true);
              signOut();
            });
        } else if (getProviderFromUser(eventUser) === GOOGLE_PROVIDER_ID) {
          // Intentionally avoid use of isGoogleUser() in order to disable allowDev
          setUser(eventUser);
          setCleverUserId(null);

          signInGoogleComplete(eventUser).catch((error) => {
            // eslint-disable-next-line no-console
            console.log(error);
            // eslint-disable-next-line no-alert
            alert(
              'An error occurred completing sign in. Try again or notify your teacher. If you are an educator and need support, please visit the FAQ for further assistance.'
            );
            throw new Error('Unable to complete login');
          });
        } else {
          setUser(eventUser);
          setCleverUserId(null);

          // Handle class code on login
          const classCode = getClassCode();
          if (classCode) {
            joinClassroom(classCode)
              .then(() => {
                removeClassCode();
              })
              .catch(() => {
                // error silently
              });
          }
        }
      } else {
        setUser(null);
        setCleverUserId(null);
        setIsLoaded(true);

        // Remove class code on log out
        removeClassCode();
        clearApiCache();
      }
    });

    return authEventHandler;
  }, []);

  /**
   * Create Firebase Firestore event handler to listen for changes in profile state
   */
  useEffect(() => {
    if (user) {
      // Firebase password users will use uid for profile key.
      // Firebase OIDC (Clever) users will use clever id for profile key.
      const unsub = onSnapshot(doc(db, profileDatabaseName, cleverUserId || user.uid), (row) => {
        const data = row.data();

        if (data) {
          setProfile({ ...defaultProfile(), ...data });
        } else {
          setProfile(defaultProfile());
        }

        setIsLoaded(true);
      });

      return unsub;
    }

    setProfile(defaultProfile());
    return () => {};
  }, [user, cleverUserId]);

  useEffect(() => {
    if (user && profile.username && isLoaded) {
      // User is loaded and/or profile is updated
      // Note: This will get called every profile update.

      // Verify user has badges, if not apply default badges
      if (!profile.badges || profile.badges.length === 0) {
        getBadgesByTrigger('action', 'createProfile').forEach((badge) => {
          // eslint-disable-next-line no-use-before-define
          addBadgeProgress(badge.id, 'profile');
        });
      }
    }
  }, [isLoaded, profile, user]);

  /**
   * Create object to pass via context
   *
   * @returns {Object}
   */
  const value = useMemo(
    () => ({
      isLoaded,
      isSignedIn: user && profile.username && isLoaded,
      user,
      profile,
      stashURL,
      signUp,
      getSchoolFromId,
      signUpAdditionalData,
      signIn,
      signOut,
      setPassword,
      reauthenticate,
      sendLink,
      acceptLink,
      setEmail,
      setAvatar,
      setWatchedIntro,
      setTakenQuestionnaire,
      setCompletedSkills,
      setConsumedContent,
      setFavoriteContent,
      setSkills,
      setOptIns,
      addBadgeProgress,
      addPowerSkill,
      addQuickTipAlert,
      redirectStashURL,
      setStashURL,
      signInGoogle,
      signInGoogleRedirect,
      signInGooglePopup,
      signInClever,
      signInCleverRedirect,
      isPasswordUser: isPasswordUser(),
      isCleverUser: isCleverUser(),
      isGoogleUser: isGoogleUser(),
      dashboardType: dashboardType(),
      isDashboardTypeClever: dashboardType() === DASHBOARD_TYPE_CLEVER,
      isDashboardTypeManual: dashboardType() === DASHBOARD_TYPE_MANUAL,
      provider: getProviderFromUser(user),
      validateEmail,
    }),
    [isLoaded, user, profile, stashURL]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
